import { checkIsNumber } from '@apeiron/common'
import {
  CurrencyMoneyType,
  CurrencyTokenType,
  useCurrencyContext
} from '@apeiron/currency'
import { noForwardProps, useWeb3 } from '@apeiron/temp'
import { ApeironNumberTextField } from '@apeiron/ui'
import { Box, ButtonBase, Divider, styled, Typography } from '@mui/material'
import VestingSummaryDataSection from '@src/components/reward/vesting/VestingSummaryDataSection'
import ApeironLoadingButton from '@src/components/share/apeiron/ApeironLoadingButton'
import { useVesting } from '@src/hooks/contract/useVesting'
import { VestData } from '@src/types/vesting'
import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
`

const Token = styled(ApeironNumberTextField)`
  .MuiInputBase-root {
    background-color: #202020;
    border: 1px solid #4b4b4b;
  }
  .MuiOutlinedInput-input {
    background-color: #202020;
  }
  height: unset;
  margin-bottom: 0px;
  max-width: 300px;
  width: 100%;
`

const StartAdornment = styled(Typography)`
  color: #8b8b8b;
  font-weight: 300;
  margin-right: 8px;
  margin-top: 2px;
`

const EndAdornment = styled(Typography)`
  color: white;
  font-weight: 300;
  margin-left: 8px;
  margin-top: 2px;
  white-space: nowrap;
  display: flex;
  column-gap: 10px;
`

const AdornmentDevider = styled(Divider)`
  border-color: #838383;
`

const MaxButton = styled(ButtonBase, noForwardProps)<MaxButtonProps>`
  ${props =>
    props.__max ? '--color-code: #575758;' : '--color-code: #ffffff;'}
  color: var(--color-code);
  font-size: 14px;
  padding: 0px;
  background-color: transparent;
  text-transform: uppercase;
  :hover {
    background-color: transparent;
    color: #74a6ea;
  }
`

const ClaimButton = styled(ApeironLoadingButton)`
  font-size: 16px;
  font-weight: 300;
  margin-top: auto;
  max-width: 180px;
  min-width: 120px;
`

const VestingSummaryClaimableSection = (props: Props) => {
  const { vestData, onClaimed } = props

  const { t } = useTranslation()

  const { getApeironPrice, getCurrency, getExchangeRate, getUSDPrice } =
    useCurrencyContext()

  const { claim } = useVesting()

  const { fromWei, toWei } = useWeb3()

  const { control, handleSubmit, getValues, setValue } = useForm()

  const tokenInput = useWatch({ name: 'token', control })

  const [claiming, setClaiming] = useState<boolean>(false)

  const currency = getCurrency(vestData.currency?.type)

  const availableNonWei = parseInt(
    currency ? fromWei(vestData.tokenClaimable, currency.decimals) : '0'
  )

  const claimable = useMemo(() => {
    if (vestData.loading) {
      return false
    }

    const input = parseFloat(tokenInput)

    return input > 0 && input <= availableNonWei
  }, [availableNonWei, tokenInput, vestData.loading])

  const handleOnTokenChange = useCallback(() => {
    const token = getValues().token

    if (R.isNotNil(vestData.currency) && checkIsNumber(token)) {
      const rate = getExchangeRate(
        vestData.currency.type,
        CurrencyMoneyType.Usd
      )

      const usd = rate * parseFloat(token)

      setValue('usd', usd)
    } else {
      setValue('usd', '0')
    }
  }, [getExchangeRate, getValues, setValue, vestData.currency])

  const handleOnMax = useCallback(() => {
    setValue('token', availableNonWei)
    handleOnTokenChange()
  }, [availableNonWei, handleOnTokenChange, setValue])

  const doSubmit = useCallback(async () => {
    if (vestData.currency) {
      setClaiming(true)

      const claimAmount = toWei(tokenInput, vestData.currency.decimals)

      const success = await claim(claimAmount)

      if (success && onClaimed) {
        onClaimed()
      }

      setClaiming(false)
    }
  }, [vestData.currency, toWei, tokenInput, claim, onClaimed])

  const claimableUsd = getUSDPrice(
    vestData.currency?.type,
    vestData.tokenClaimable,
    true
  )

  return (
    <Form onSubmit={handleSubmit(doSubmit)}>
      <VestingSummaryDataSection
        content={{
          remark: `$${claimableUsd}`,
          subtext: currency?.name || '',
          text: getApeironPrice(
            CurrencyTokenType.Aprs,
            vestData.tokenClaimable,
            { allowedLargeNumberAbbr: false, maxLength: 10 }
          )
        }}
        info={t('vesting.calculated_by')}
        title={t('vesting.claimable')}
      />
      <Box>
        <Box>
          <Token
            control={control}
            defaultValue={0}
            endAdornment={
              <EndAdornment>
                <Box>{currency?.name}</Box>
                <AdornmentDevider orientation={'vertical'} flexItem />
                <MaxButton
                  __max={availableNonWei === tokenInput}
                  onClick={handleOnMax}
                >
                  {t('common.max')}
                </MaxButton>
              </EndAdornment>
            }
            name={'token'}
            startAdornment={
              <StartAdornment>{t('vesting.amount')}</StartAdornment>
            }
            onValueChanged={handleOnTokenChange}
          />
        </Box>
        <ClaimButton disabled={!claimable} loading={claiming} type='submit'>
          {t('common.button.claim')}
        </ClaimButton>
      </Box>
    </Form>
  )
}

type MaxButtonProps = {
  __max?: boolean
}

type Props = {
  vestData: VestData
  onClaimed?: () => void
}

export default VestingSummaryClaimableSection
